import type { AgentId } from '@/config/env'
import type {
  AssistantMessageDisplay,
  ChatEvent,
  ThinkingMessageDisplay,
  ToolCallDisplay,
} from '@/types'

import { CLARIFYING_TOOL_NAME } from './ClarifyingQuestionMessage'

// Render-time constructs only: the stream reducer and persisted history stay
// flat lists of ChatEvent; grouping happens at the single render choke point
// in AgentChat.
export interface ToolCallGroupDisplay {
  type: 'tool_call_group'
  id: string
  toolCalls: ToolCallDisplay[]
}

type StepEvent =
  | AssistantMessageDisplay
  | ThinkingMessageDisplay
  | ToolCallDisplay

export type WorkStep = StepEvent | ToolCallGroupDisplay

export interface WorkBlockDisplay {
  type: 'work_block'
  // Derived from the first event so it stays stable while a streaming block
  // grows, and React keeps it mounted.
  id: string
  steps: WorkStep[]
  startedAt: Date
  // When the work ended; undefined while the block trails a turn in progress.
  endedAt?: Date
}

export type RenderableItem = ChatEvent | WorkBlockDisplay

// The trailing block of the active stream is live; every other block is done.
export type BlockPhase = 'live' | 'done'

// note_feedback fires on David's judgement, so surfacing it would show the user
// a message they did not ask for and cannot act on.
const HIDDEN_TOOL_NAME = 'note_feedback'

// User messages and clarifying-question calls (which render their own
// interactive card) end a turn; every other event can sit inside a block.
function isStepEvent(event: ChatEvent): event is StepEvent {
  return (
    event.type !== 'user_message' &&
    !(event.type === 'tool_call' && event.toolName === CLARIFYING_TOOL_NAME)
  )
}

function appendStep(steps: WorkStep[], event: StepEvent) {
  const last = steps.at(-1)
  if (event.type === 'tool_call' && last?.type === 'tool_call_group') {
    last.toolCalls.push(event)
  } else if (event.type === 'tool_call' && last?.type === 'tool_call') {
    steps[steps.length - 1] = {
      type: 'tool_call_group',
      id: `tool-group-${last.id}`,
      toolCalls: [last, event],
    }
  } else {
    steps.push(event)
  }
}

function lastWorkTime(event: StepEvent): Date {
  return (event.type === 'tool_call' && event.endTime) || event.timestamp
}

function foldTurn(
  turn: StepEvent[],
  opener: ChatEvent | undefined,
  boundary: ChatEvent | undefined
): RenderableItem[] {
  const lastWork = turn.findLastIndex(
    (event) => event.type !== 'assistant_message'
  )
  const first = turn[0]
  const lastWorkEvent = turn[lastWork]
  if (!first || !lastWorkEvent) return turn

  const block: WorkBlockDisplay = {
    type: 'work_block',
    id: `work-${first.id}`,
    steps: [],
    // Persisted agent messages are stamped close together, so the user's
    // message is the honest start of the work.
    startedAt:
      opener?.type === 'user_message' ? opener.timestamp : first.timestamp,
  }
  for (const event of turn.slice(0, lastWork + 1))
    appendStep(block.steps, event)
  const after = turn.slice(lastWork + 1)
  // A user message can arrive long after a run that stopped mid-work, so only
  // agent output marks when the work ended.
  block.endedAt =
    after[0]?.timestamp ??
    (boundary?.type === 'user_message'
      ? lastWorkTime(lastWorkEvent)
      : boundary?.timestamp)
  return [block, ...after]
}

/**
 * Folds each turn (the events between user messages and clarifying-question
 * calls) into one work block that runs from the turn's first event to its last
 * thought or tool call, so prose written between steps folds away too. What
 * follows the last work event is the answer and stays outside the block. A
 * turn with no work renders flat. Inside a block, 2+ consecutive tool calls
 * form a group; a lone call stays bare. Order is preserved exactly.
 *
 * David only: every other agent keeps its flat per-event rendering, so their
 * events pass through unchanged.
 */
export function groupEvents(
  events: ChatEvent[],
  agentId: AgentId
): RenderableItem[] {
  if (agentId !== 'david') return events

  const items: RenderableItem[] = []
  let turn: StepEvent[] = []
  let opener: ChatEvent | undefined

  for (const event of events) {
    // Skipped rather than filtered upstream so the calls around it stay in
    // one group.
    if (event.type === 'tool_call' && event.toolName === HIDDEN_TOOL_NAME)
      continue

    if (isStepEvent(event)) {
      turn.push(event)
    } else {
      items.push(...foldTurn(turn, opener, event), event)
      turn = []
      opener = event
    }
  }
  items.push(...foldTurn(turn, opener, undefined))

  return items
}
